import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Card } from '../design-system/components/Card';
import { Button } from '../design-system/components/Button';
import { Heading, Text } from '../design-system/components/Typography';
import { Stack, Container } from '../design-system/components/Layout';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import {
  applicationStatusService,
  ApplicationStatusResponse,
  normalizeRegistrationStatus,
} from '../services/applicationStatus';
import { useToast } from '../hooks/useToast';

const PageContainer = styled(Container)`
  max-width: 640px;
  padding: ${spacing[10]} ${spacing[5]};
  background: ${props => props.theme.colors.gradientBackground};
`;

const StyledCard = styled(Card)`
  padding: ${spacing[8]};
`;

const InputGroup = styled.div`
  margin-bottom: ${spacing[5]};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${spacing[2]};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.theme.colors.text};
`;

const Input = styled.input`
  width: 100%;
  padding: ${spacing[3]} ${spacing[4]};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme.colors.text};
  background: ${props => props.theme.colors.surface};
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ResultBlock = styled.div<{ $tone: 'pending' | 'approved' | 'rejected' | 'changes' }>`
  margin-top: ${spacing[6]};
  padding: ${spacing[5]};
  border-radius: 8px;
  background: ${props => {
    if (props.$tone === 'approved') return props.theme.colors.success + '20';
    if (props.$tone === 'rejected') return props.theme.colors.error + '20';
    if (props.$tone === 'changes') return (props.theme.colors.info || props.theme.colors.primary) + '18';
    return props.theme.colors.surfaceHover;
  }};
  border-left: 4px solid ${props => {
    if (props.$tone === 'approved') return props.theme.colors.success;
    if (props.$tone === 'rejected') return props.theme.colors.error;
    if (props.$tone === 'changes') return props.theme.colors.info || props.theme.colors.primary;
    return props.theme.colors.warning;
  }};
`;

const Timeline = styled.ol`
  margin: ${spacing[4]} 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const TimelineItem = styled.li<{ $active?: boolean; $done?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  font-size: ${typography.fontSize.sm};
  color: ${props =>
    props.$active || props.$done ? props.theme.colors.text : props.theme.colors.textSecondary};
  font-weight: ${props =>
    props.$active ? typography.fontWeight.semibold : typography.fontWeight.normal};

  &::before {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${props => {
      if (props.$done) return props.theme.colors.success;
      if (props.$active) return props.theme.colors.primary;
      return props.theme.colors.border;
    }};
    flex-shrink: 0;
  }
`;

const StyledLink = styled(Link)`
  color: ${props => props.theme.colors.primary};
  font-weight: ${typography.fontWeight.semibold};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

function statusTone(status: number): 'pending' | 'approved' | 'rejected' | 'changes' {
  if (status === 2) return 'approved';
  if (status === 3) return 'rejected';
  if (status === 4) return 'changes';
  return 'pending';
}

const ApplicationStatus: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { error: showError } = useToast();
  const [searchParams] = useSearchParams();
  const [reference, setReference] = useState(searchParams.get('reference') || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ApplicationStatusResponse | null>(null);

  const statusNum = useMemo(
    () => (result ? normalizeRegistrationStatus(result.status) : 0),
    [result]
  );

  const statusLabel = (status: number) => {
    if (status === 2) return t('applicationStatus.statusApproved');
    if (status === 3) return t('applicationStatus.statusRejected');
    if (status === 4) return t('applicationStatus.statusNeedsChanges');
    return t('applicationStatus.statusPending');
  };

  const lookup = async (ref: string) => {
    setLoading(true);
    setResult(null);
    try {
      const data = await applicationStatusService.getByReference(ref.trim());
      setResult(data);
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : t('applicationStatus.notFound'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initial = searchParams.get('reference');
    if (initial?.trim()) {
      setReference(initial);
      lookup(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reference.trim()) {
      showError(t('applicationStatus.enterReference'));
      return;
    }
    await lookup(reference);
  };

  const handleEditAndResubmit = async () => {
    if (!result) return;
    const ref = encodeURIComponent(result.applicationReferenceNumber);
    let type = (result.type || '').toLowerCase();
    if (!type && result.canEdit) {
      try {
        const editable = await applicationStatusService.getEditable(
          result.applicationReferenceNumber
        );
        type = (editable.type || '').toLowerCase();
      } catch {
        // Fall through to individual route if type cannot be resolved.
      }
    }
    if (type === 'institution') {
      navigate(`/register-institution?mode=resubmit&reference=${ref}`);
    } else {
      navigate(`/register-individual?mode=resubmit&reference=${ref}`);
    }
  };

  const feedback = result?.adminFeedback || result?.rejectionReason;

  return (
    <PageContainer>
      <StyledCard variant="elevated" headerVariant="gradient" accentBorder="top" pattern="dots" patternIntensity="subtle">
        <Stack direction="column" spacing="lg">
          <Heading variant="h2" color="text">
            {t('applicationStatus.title')}
          </Heading>
          <Text size="sm" color="secondary">
            {t('applicationStatus.subtitle')}
          </Text>

          <form onSubmit={handleSubmit}>
            <InputGroup>
              <Label htmlFor="reference">{t('applicationStatus.referenceLabel')}</Label>
              <Input
                id="reference"
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="APP-2026-00001"
                disabled={loading}
              />
            </InputGroup>
            <Button type="submit" variant="gradient" loading={loading} disabled={loading}>
              {t('applicationStatus.checkStatus')}
            </Button>
          </form>

          {result && (
            <ResultBlock $tone={statusTone(statusNum)}>
              <Text size="sm" color="secondary" style={{ marginBottom: spacing[2] }}>
                {t('applicationStatus.reference')}:{' '}
                <strong>{result.applicationReferenceNumber}</strong>
              </Text>
              <Text size="base" style={{ marginBottom: spacing[2] }}>
                {t('applicationStatus.status')}: <strong>{statusLabel(statusNum)}</strong>
              </Text>
              {result.submittedAt && (
                <Text size="sm" color="secondary" style={{ marginBottom: spacing[2] }}>
                  {t('applicationStatus.submittedAt')}:{' '}
                  {new Date(result.submittedAt).toLocaleDateString(i18n.language)}
                </Text>
              )}

              <Timeline aria-label={t('applicationStatus.progress')}>
                <TimelineItem $done $active={statusNum === 1}>
                  {t('applicationStatus.stepSubmitted')}
                </TimelineItem>
                <TimelineItem
                  $done={statusNum === 2 || statusNum === 3}
                  $active={statusNum === 1 || statusNum === 4}
                >
                  {t('applicationStatus.stepReview')}
                </TimelineItem>
                {statusNum === 4 && (
                  <TimelineItem $active>{t('applicationStatus.stepNeedsChanges')}</TimelineItem>
                )}
                <TimelineItem $done={statusNum === 2} $active={statusNum === 2}>
                  {t('applicationStatus.stepDecision')}
                </TimelineItem>
              </Timeline>

              {feedback && (
                <div style={{ marginTop: spacing[4] }}>
                  <Text size="sm" color="secondary">
                    {statusNum === 4
                      ? t('applicationStatus.changesRequested')
                      : t('applicationStatus.rejectionReason')}
                  </Text>
                  <Text size="sm" style={{ marginTop: spacing[2], whiteSpace: 'pre-wrap' }}>
                    {feedback}
                  </Text>
                </div>
              )}

              {result.canEdit && (
                <div style={{ marginTop: spacing[5] }}>
                  <Button variant="primary" onClick={handleEditAndResubmit}>
                    {t('applicationStatus.editAndResubmit')}
                  </Button>
                </div>
              )}
            </ResultBlock>
          )}

          <Text size="sm" color="secondary">
            <StyledLink to="/login">{t('applicationStatus.backToLogin')}</StyledLink>
            {' · '}
            <StyledLink to="/register">{t('register.back')}</StyledLink>
          </Text>
        </Stack>
      </StyledCard>
    </PageContainer>
  );
};

export default ApplicationStatus;
